import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { getSchoolForSession, getSchoolIdFromResponse } from '../utils/schoolHelper';
import { getApiBaseUrl } from '../apiBase';
import DemoViewBanner from '../demo/DemoViewBanner';

const API = getApiBaseUrl();

function firstDayOfMonth() {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function todayStr() {
    return new Date().toISOString().slice(0, 10);
}

export default function ReportsPage() {
    const { t } = useTranslation(['reports', 'common', 'errors', 'messages']);
    const staffRoleLabel = (role) => t(`reports:roles.${role}`, { defaultValue: role });
    const [school, setSchool] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
    const [dateTo, setDateTo] = useState(todayStr);

    const token = localStorage.getItem('token');

    useEffect(() => {
        const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
        (async () => {
            setLoading(true);
            setErr('');
            try {
                const schoolRes = await getSchoolForSession(api);
                const schoolId = getSchoolIdFromResponse(schoolRes);
                setSchool(schoolRes.data);
                const dash = await api.get('/reports/dashboard', {
                    params: {
                        schoolId,
                        dateFrom,
                        dateTo
                    }
                });
                setData(dash.data);
            } catch (e) {
                setErr(e.response?.data?.message || t('reports:loadError'));
                setData(null);
            }
            setLoading(false);
        })();
    }, [dateFrom, dateTo, token]);

    async function refresh() {
        if (!school?.id) return;
        const api = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
        setLoading(true);
        setErr('');
        try {
            const dash = await api.get('/reports/dashboard', {
                params: { schoolId: school.id, dateFrom, dateTo }
            });
            setData(dash.data);
        } catch (e) {
            setErr(e.response?.data?.message || t('reports:updateError'));
        }
        setLoading(false);
    }

    const card = {
        minHeight: 'min(440px, 52vh)',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box'
    };
    const h2 = { margin: '0 0 12px', fontSize: '16px', fontWeight: 800, color: '#1e293b' };
    const muted = { fontSize: '12px', color: '#64748b', lineHeight: 1.45 };

    return (
        <OwnerPageLayout
            title={t('reports:title')}
            subtitle={school ? t('reports:subtitleSchool', { name: school.name }) : t('reports:subtitle')}
            maxWidth={1200}
            beforeMain={<DemoViewBanner />}
        >
                <div className="sp-panel sp-toolbar" style={{ marginBottom: 20, padding: 14 }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#475569' }}>
                        {t('reports:dateFrom')}
                        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
                    </label>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#475569' }}>
                        {t('reports:dateTo')}
                        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: '8px 10px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
                    </label>
                    <button type="button" className="sp-btn sp-btn--primary" onClick={refresh}>
                        {t('reports:refresh')}
                    </button>
                </div>

                {loading && <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>{t('common:loading')}</div>}
                {err && !loading && (
                    <div style={{ padding: '16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', color: '#991b1b' }}>
                        {err}
                    </div>
                )}

                {data && !loading && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
                        gap: '16px'
                    }}>
                        {/* Посещаемость */}
                        <section className="sp-panel" style={card}>
                            <h2 style={h2}>{t('reports:sectionAttendance')}</h2>
                            <p style={{ ...muted, margin: '0 0 12px' }}>
                                {t('reports:sectionAttendanceDesc')}
                            </p>
                            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
                                <Kpi label={t('reports:kpiTotal')} value={data.attendance?.totalRecords ?? 0} color="#1d4ed8" />
                                <Kpi label={t('reports:kpiPresent')} value={data.attendance?.presentCount ?? 0} color="#059669" />
                                <Kpi label={t('reports:kpiAbsent')} value={data.attendance?.absentCount ?? 0} color="#dc2626" />
                            </div>
                            <div style={{ overflowX: 'auto', flex: 1 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                                            <th style={{ padding: '8px 6px' }}>{t('reports:colGroup')}</th>
                                            <th style={{ padding: '8px 6px' }}>{t('reports:colMarks')}</th>
                                            <th style={{ padding: '8px 6px' }}>{t('reports:colWas')}</th>
                                            <th style={{ padding: '8px 6px' }}>{t('reports:colWasNot')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data.attendance?.byGroup || []).map(g => (
                                            <tr key={g.groupId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '8px 6px' }}>{g.groupName || '—'}</td>
                                                <td style={{ padding: '8px 6px' }}>{g.records}</td>
                                                <td style={{ padding: '8px 6px', color: '#059669' }}>{g.present}</td>
                                                <td style={{ padding: '8px 6px', color: '#b91c1c' }}>{g.absent}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {(data.attendance?.byGroup || []).length === 0 && (
                                    <div style={{ ...muted, padding: '12px 0' }}>{t('reports:noDataPeriod')}</div>
                                )}
                            </div>
                        </section>

                        {/* Расходы и оплаты */}
                        <section className="sp-panel" style={card}>
                            <h2 style={h2}>{t('reports:sectionMoney')}</h2>
                            <p style={{ ...muted, margin: '0 0 12px' }}>
                                {t('reports:sectionMoneyDesc')}
                            </p>
                            <div style={{ display: 'grid', gap: '10px', fontSize: '14px' }}>
                                <Row label={t('reports:rowSubscriptions')} value={`${(data.money?.subscriptionInflow ?? 0).toLocaleString('ru-RU')} ₽`} strong />
                                <Row label={t('reports:rowLessons')} value={String(data.money?.lessonDeductionsCount ?? 0)} />
                                <Row label={t('reports:rowEventCommon')} value={`${(data.money?.eventCommonCosts ?? 0).toLocaleString('ru-RU')} ₽`} />
                                <Row label={t('reports:rowEventPersonal')} value={`${(data.money?.eventPersonalCosts ?? 0).toLocaleString('ru-RU')} ₽`} />
                                <div style={{
                                    marginTop: '8px',
                                    paddingTop: '12px',
                                    borderTop: '2px solid #e2e8f0',
                                    fontWeight: 800,
                                    fontSize: '16px',
                                    color: '#0f172a'
                                }}>
                                    {t('reports:eventCostsTotal', { amount: (data.money?.eventCostsTotal ?? 0).toLocaleString('ru-RU') })}
                                </div>
                            </div>
                        </section>

                        {/* Мероприятия */}
                        <section className="sp-panel" style={card}>
                            <h2 style={h2}>{t('reports:sectionEvents')}</h2>
                            <p style={{ ...muted, margin: '0 0 12px' }}>
                                {t('reports:sectionEventsDesc')}
                            </p>
                            <div style={{ fontSize: '28px', fontWeight: 800, color: '#4f46e5', marginBottom: '14px' }}>
                                {data.events?.count ?? 0} <span style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>{t('reports:eventsCount')}</span>
                            </div>
                            <div style={{ overflowX: 'auto', flex: 1 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                                            <th style={{ padding: '8px 6px' }}>{t('reports:colTitle')}</th>
                                            <th style={{ padding: '8px 6px' }}>{t('reports:colDate')}</th>
                                            <th style={{ padding: '8px 6px' }}>{t('reports:colCity')}</th>
                                            <th style={{ padding: '8px 6px' }}>Σ ₽</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data.events?.topByCost || []).map((ev, i) => (
                                            <tr key={`${ev.title}_${i}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '8px 6px' }}>{ev.title}</td>
                                                <td style={{ padding: '8px 6px' }}>{ev.startDate}</td>
                                                <td style={{ padding: '8px 6px' }}>{ev.city || '—'}</td>
                                                <td style={{ padding: '8px 6px', fontWeight: 600 }}>{(ev.total ?? 0).toLocaleString('ru-RU')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {(data.events?.topByCost || []).length === 0 && (
                                    <div style={{ ...muted, padding: '12px 0' }}>{t('reports:noEventsPeriod')}</div>
                                )}
                            </div>
                        </section>

                        {/* Сотрудники */}
                        <section className="sp-panel" style={card}>
                            <h2 style={h2}>{t('reports:sectionStaff')}</h2>
                            <p style={{ ...muted, margin: '0 0 12px' }}>
                                {t('reports:sectionStaffDesc')}
                            </p>
                            <div style={{ fontSize: '28px', fontWeight: 800, color: '#0d9488', marginBottom: '14px' }}>
                                {data.staff?.totalActive ?? 0} <span style={{ fontSize: '14px', fontWeight: 600, color: '#64748b' }}>{t('reports:staffCount')}</span>
                            </div>
                            <div style={{ overflowX: 'auto', flex: 1 }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
                                            <th style={{ padding: '8px 6px' }}>{t('reports:colRole')}</th>
                                            <th style={{ padding: '8px 6px' }}>{t('reports:colCount')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(data.staff?.byRole || []).map(r => (
                                            <tr key={r.role} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                <td style={{ padding: '8px 6px' }}>{staffRoleLabel(r.role)}</td>
                                                <td style={{ padding: '8px 6px', fontWeight: 600 }}>{r.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {(data.staff?.byRole || []).length === 0 && (
                                    <div style={{ ...muted, padding: '12px 0' }}>{t('reports:noActiveStaff')}</div>
                                )}
                            </div>
                        </section>
                    </div>
                )}
        </OwnerPageLayout>
    );
}

function Kpi({ label, value, color }) {
    return (
        <div style={{
            padding: '10px 14px',
            borderRadius: '10px',
            background: `${color}12`,
            border: `1px solid ${color}33`
        }}>
            <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>{label}</div>
            <div style={{ fontSize: '22px', fontWeight: 800, color }}>{value}</div>
        </div>
    );
}

function Row({ label, value, strong }) {
    return (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', borderBottom: '1px solid #f1f5f9', paddingBottom: '8px' }}>
            <span style={{ color: '#475569' }}>{label}</span>
            <span style={{ fontWeight: strong ? 700 : 500, color: '#0f172a' }}>{value}</span>
        </div>
    );
}
